import styled from '@emotion/styled';
import {
    Badge,
    BodyTextMedium,
    ButtonGroup,
    HeadingText60,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { Container, IconContainer } from '../components/Common';
import { useUpdateMemberProfileMutation } from '../documents/addDirectTherapyAccess.generated';
import { useAnalytics } from '@modernhealth/web-analytics';
import { Button } from 'src/shared/analytics/components';
import { getMostRecentCareRecommendation } from 'src/modules/user/selectors/userSelectors';
import { captureException } from 'src/shared/helpers/captureException';

export const DirectTherapyAccessPage: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { therapyVisitsRemaining } = useSelector(getSessionUtilizationInfo);
    const analytics = useAnalytics();
    const [updateMemberProfileMutation] = useUpdateMemberProfileMutation({
        variables: {
            memberProfile: {
                therapyAccessShortcut: true,
            },
        },
    });

    const latestRecommendation = useSelector(getMostRecentCareRecommendation);

    const addDirectTherapyAccess = async () => {
        const { errors } = await updateMemberProfileMutation();
        if (errors && errors.length > 0) {
            Sentry.captureException(errors);
            captureException(errors);
            toastNotificationsService.renderErrorToast(
                `${t('messaging:generic_error.title')}. ${t(
                    'messaging:generic_error.description',
                )}`,
            );
            return;
        }

        navigationService.navigate('/work-with-a-therapist?disableCheck=true');
    };

    return (
        <Container>
            <IconContainer
                onClick={() => {
                    analytics.trackClick('direct_therapy_access.cancel');
                    history.goBack();
                }}
            >
                <Icon name="arrow-left" />
            </IconContainer>
            <img
                src="https://assets.joinmodernhealth.com/homescreen/actions/care_cards/work-with-therapist-triplet.png"
                alt=""
            />
            <StyledBadge>
                <Badge
                    variant="success"
                    title={t('videoSessions:sessions_widget.session_available_plural', {
                        count: therapyVisitsRemaining,
                    })}
                    size="medium"
                />
            </StyledBadge>

            <HeadingText60 marginTop={20}>
                {t('providers:intro.work_with.title.therapist')}{' '}
            </HeadingText60>
            <Wrapper>
                <BodyTextMedium>{t('care:confirm_therapy')}</BodyTextMedium>

                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            eventName="direct_therapy_access.cancel"
                            fluid
                            variant="secondary"
                            onClick={() => history.goBack()}
                        >
                            {t('providers:no_therapy.not_now')}
                        </Button>
                        <Button
                            eventName="direct_therapy_access.confirm"
                            eventProperties={{
                                care_recomemendation: latestRecommendation?.recommendation ?? null,
                            }}
                            onClick={() => addDirectTherapyAccess()}
                            fluid
                        >
                            {t('translation:common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </Wrapper>
        </Container>
    );
};

const StyledBadge = styled.div`
    margin-top: 20px;
    background-color: ${colors.cyan10};
    border-radius: ${pxToRem(120)};
    font-size: ${pxToRem(22)};
    display: flex;
    align-items: center;
    padding: ${pxToRem(2, 5)};
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    max-width: ${pxToRem(500)};
`;

const ButtonGroupContainer = styled.div`
    margin-top: ${pxToRem(20)};
`;
